<script>
  import { APP_NAME, DOMAIN_LOCK } from '@src/app.config'
  import {
    resolveVisibility
  } from '@utils/oneTab'
  import {
    authComputed,
    errorsComputed,
    userMethods,
  } from '@state/helpers'

  const FIVE_MINS_IN_MILLISECONDS = 5*60*1000;
  const ONE_WEEK_IN_MILLISECONDS = 7*24*60*60*1000;

  export default {
    page: {
      // All subcomponent titles will be injected into this template.
      titleTemplate(title) {
        title = typeof title === 'function' ? title(this.$store) : title
        return title ? `${title} | ${APP_NAME}` : APP_NAME
      },
    },
    data() {
      return {
        instanceUUID: null,
        storage: null,
        defaults:{
          expire: {
            title: "Session Expiry",
            message: "Your session has expired, please re-login.",
            ok: "Re-Login",
            logout: true,
          },
          remote: {
            title: "Remote Access",
            message: "Your account has logged in from another browser, please re-authenticate.",
            ok: "Re-Login",
            logout: true,
          },
          timeout: {
            title: "Server Timeout",
            message: "Looks like the server is running a tad slow, Please try again in 30 seconds.",
            ok: "Understood",
            logout: false,
          },
        },
        customModal: {}
      }
    },
    computed: {
      ...authComputed,
      ...userMethods,
      ...errorsComputed,
      lastSync(){
        return this.currentUser.lastUpdate;
      },
      fourZeroNined(){
        return this.getErrors[409];
      },
      reAuthAttempts(){
        return this.$store.state.auth.reAuthAttempts
      },
      initLoginTime(){
        return this.$store.state.auth.loginEpoch
      },
      expireLoginTime(){
        return this.initLoginTime + ONE_WEEK_IN_MILLISECONDS
      }
    },
    watch:{
      lastSync(newValue, oldValue){
        this.checkIfUserExpiring(newValue, this.currentUser.token_expires, this.currentUser.token_refresh_expires)
      },
      isDeAuthed(newValue, oldValue){
        if (newValue === true){
          this.customModal = {...this.defaults.remote}
          window.dataLayer?.push({
            event: 'force_logout',
            user_hash: this.$store.state.auth.hashedUserName,
          });
          this.$customModal.show('relog-modal')
        }
      },
      fourZeroNined(newValue, oldValue){
        if (newValue === true){
          this.customModal = {...this.defaults.timeout}
          console.log(`AUTH: Login for ${this.$store.state.auth.activeUserName} was detached due to expiry at ${Date.now()}`);
          this.$customModal.show('relog-modal')
          setTimeout(el => this.$store.dispatch('errors/setFourZeroNine', false), (20 * 1000)) // 15 seconds
        } else {
          window.localStorage.setItem('lilberd-tmr-active', JSON.stringify(false))
          window.localStorage.setItem('lilberd-tmr-start', JSON.stringify(''))
        }
      },
    },
    created(){
      this.customModal = { ...this.defaults.expire };
      // generate single Vue storage accessor
      const storageObj = window.localStorage
      this.$set(this, 'storage', storageObj)

      // generate my instances uuid
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      this.$set(this, 'instanceUUID', array[0])

      // init uuid into local-storage
      this.storage.setItem('lilberd-active-uuid', JSON.stringify(this.instanceUUID))

      this.$store.commit('tab/SET_UUID',this.instanceUUID)
      this.$store.commit('tab/SET_WARNING', false )

      // add listener for tab enter / leave
      document.addEventListener("visibilitychange", this.visibilityCallback);
      document.addEventListener('keydown', this.keyDownCallback)
      document.addEventListener('keyup',  this.keyUpCallback)

      // add the subdomain as a class to the body, to allow for dynamic theming
      if (DOMAIN_LOCK){
        this.addDomainAsClassToElement(document.body, DOMAIN_LOCK);
      }
    },
    methods: {
      resolveVisibility,
      addDomainAsClassToElement(element, domainKey){
        let subdomain = ''
        if (document && document.domain && document.domain.includes(domainKey)){
          const domainSegments = document.domain.split('.')
          const subdomainIndex = domainSegments.findIndex(el => el === domainKey)
          subdomain = domainSegments.reduce(((a,b, index) => {
            if (index < subdomainIndex)
              return `${a}.${b}`
            return `${a}`
          }))
        }
        if (subdomain){
          element.classList.add(subdomain)
        }
      },
      keyDownCallback(event){
        if (event.ctrlKey && !this.$store.state.keypress.isCtrl){
          this.$store.commit('keypress/SET_CONTROL', true )
        }
      },
      keyUpCallback(event){
        if (event.key === 'Control'){
          this.$store.commit('keypress/SET_CONTROL', false )
        }
      },
      visibilityCallback(){
        // if the warning is already up then the user is already aware so no need to re-prompt
        if(!this.$store.state.tab.showWarning){
          this.resolveVisibility(this.storage, this.instanceUUID)
        }else{
          // refresh current tab UUID in storage
          this.storage.setItem('lilberd-active-uuid', JSON.stringify(this.instanceUUID))
        }
        // your visibility has either left or come into view,
        // either way you don't have control pressed currently,
        // unless you are ctrl-tab navigating
        this.$store.commit('keypress/SET_CONTROL', false )
      },
      addBanner(){
        this.$store.commit('tab/SET_WARNING', true )
      },
      reloadPage(){
        location.reload()
      },
      checkIfUserExpiring(lastUpdate, tokenExpire, refreshTokenExpire){
        const expiringThreshold = lastUpdate + FIVE_MINS_IN_MILLISECONDS

        if ((expiringThreshold >= refreshTokenExpire || expiringThreshold >= tokenExpire) && this.reAuthAttempts > 6 ||  expiringThreshold >= this.expireLoginTime ){
          console.log('client session expiring soon')
          this.$customModal.show('relog-modal')
        }
      },
      confirmOk(shouldLogout){
        if (shouldLogout){
          this.logUserOut()
        }
      },
      logUserOut(){
        this.$router.push({ name: 'logout', params: { bypassAuth: true }})
      },
    }
  }
</script>

<template>
  <div id="app">
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    <RouterView :key="$route.fullPath" />
    <BaseReloadModal
      title="Multiple Tabs Detected"
      message="We have detected that more than one instance of this application has been open in your browser. This is likely to result in changes adverse to what you are intending. \nPlease close any other open instances of this application and then reload this page.\n\nIf you are in the middle of editing an item or document, feel free to continue but please refresh the tab once you have finished."
      @reloadModalConfirmed="reloadPage"
      @reloadModalClosed="addBanner"
    />
    <BaseRelogModal
      :title="customModal.title"
      :message="customModal.message"
      :ok-text="customModal.ok"
      @relogModalConfirmed="confirmOk(customModal.logout)"
    />
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';

// Css of dependencies
@import '@cssDependency';

// Design variables and utilities from src/design.
@import '@design';

</style>
